/**
 * PII detection patterns for sanitization.
 *
 * Used by both PostHog and Sentry sanitizers to strip or mask
 * personally identifiable information before transmission.
 *
 * See Part 22 §125 of the Tech Guidelines.
 */

export const PII_PATTERNS = {
  /**
   * IBAN — two letters, two check digits, then the account in groups (with or
   * without spaces). Before the digit patterns, so no slice of it survives.
   */
  IBAN: /\b[A-Z]{2}\d{2}(?: ?[A-Z0-9]{4}){2,7}(?: ?[A-Z0-9]{1,4})?\b/g,

  /**
   * An EU VAT number with its country prefix (`PT515396010`). The bare nine
   * digits of a Portuguese NIF are caught by the nine-digit patterns below
   * (`SSN`, `BSN`); with the prefix there is no word boundary between letters
   * and digits, so they need their own.
   */
  EU_VAT:
    /\b(?:AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK|XI)U?\d{8,12}\b/g,

  /** Email addresses */
  EMAIL: /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,

  /** Phone numbers (international and domestic formats) */
  PHONE: /(\+\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,

  /** US Social Security Numbers */
  SSN: /\b\d{3}[-]?\d{2}[-]?\d{4}\b/g,

  /** Credit card numbers (4 groups of 4 digits) */
  CREDIT_CARD: /\b\d{4}[-\s]?\d{4}[-\s]?\d{4}[-\s]?\d{4}\b/g,

  /** Passport numbers (1-2 letters followed by 6-9 digits) */
  PASSPORT: /\b[A-Z]{1,2}\d{6,9}\b/g,

  /** Nine digits: a Dutch BSN, and also a Portuguese NIF. */
  BSN: /\b\d{9}\b/g,
} as const;

export type PIIType = keyof typeof PII_PATTERNS;

/**
 * Keys that should always be stripped from event properties or Sentry extras.
 */
export const SENSITIVE_KEYS = [
  'password',
  'token',
  'secret',
  'apiKey',
  'api_key',
  'authorization',
  'accessToken',
  'access_token',
  'refreshToken',
  'refresh_token',
  'creditCard',
  'credit_card',
  'ssn',
  'bsn',
  // Tax and bank identifiers (C13 / certified invoicing, 15 Oct 2026). Not
  // `nif`: the match is a substring, and `nif` sits inside `manifest` and
  // `unified`; a NIF's value is redacted by the nine-digit patterns instead.
  'taxId',
  'tax_id',
  'vatNumber',
  'vat_number',
  'iban',
] as const;
